/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Wednesday, April 13th 2022, 10:10:26 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";

interface infiniteGridScrollOptions {
  lazyLoadData: () => void;
}

// estimatedGridItemHeight - provide this so we can estimate a number of items that can fit on the page
export const useInfiniteGridScroll = (options: infiniteGridScrollOptions) => {
  const { lazyLoadData } = options;

  // Observe the last item of the current list of items. call lazyLoadData if
  // item is 50% visible in viewport (threshold=0.5) and we have more items
  // We are using a threshold of 0.5 in order to handle situations where the observable target needs a horizontal scroll
  // to be totally visible. TODO: figure out how to check Y-axis 100% threshold
  // https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API

  const lastItemPageObserver = React.useRef<IntersectionObserver>(); // using useRef so our observer can be updated between renders and thus always have access to the latest state.

  // Using a callback Ref but with hooks - https://reactjs.org/docs/refs-and-the-dom.html#callback-refs
  const setObserverTargetElementRef = React.useCallback(
    (targetElement) => {
      if (lastItemPageObserver.current) {
        lastItemPageObserver.current.disconnect();
      }
      lastItemPageObserver.current = new IntersectionObserver(
        (entries) => entries?.[0].isIntersecting && lazyLoadData(),
        { threshold: 1.0 }
      );

      targetElement && lastItemPageObserver.current.observe(targetElement);
    },
    [lazyLoadData]
  );

  return {
    setObserverTargetElementRef,
  };
};
